import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { FiEdit2, FiRefreshCw, FiRotateCcw, FiTrash2 } from "react-icons/fi";
import { usePageHeader } from "../hooks/usePageHeader";
import { LoadingSpinner } from "../Components/LoadingSpinner";
import { client } from "../Clients";
import { showSuccessModal } from "../Helper/showSuccessModal";
import GatedButton from "../Components/GatedButton";
import { purgeArchive } from "../Components/Archive/archiveClient";
import type { BCCADMINBaseModelsContentListItem, BCCADMINBaseModelsContentTypeFilterItem } from "../API/Models";

const DEFAULT_LANGUAGE = "fr";
const PAGE_SIZE = 20;

/** Archived content list with search, type filtering and deleted item restoration. */
const DeletedPages: React.FC = () => {
	const history = useHistory();
	const [items, setItems] = useState<BCCADMINBaseModelsContentListItem[]>([]);
	const [totalCount, setTotalCount] = useState(0);
	const [page, setPage] = useState(1);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [search, setSearch] = useState("");
	const [restoring, setRestoring] = useState<string | null>(null);
	const [contentTypeFilter, setContentTypeFilter] = useState("all");
	const [availableTypes, setAvailableTypes] = useState<BCCADMINBaseModelsContentTypeFilterItem[]>([]);
	const [languageFilter, setLanguageFilter] = useState("");
	const [purging, setPurging] = useState<string | null>(null);
	const [showPurgeAll, setShowPurgeAll] = useState(false);
	const [purgeAllConfirm, setPurgeAllConfirm] = useState("");
	const [purgingAll, setPurgingAll] = useState(false);

	// Permanent deletion is gated on the dedicated purge grant that ordinary editors do not hold :
	// les boutons restent visibles mais grisés-cliquables sans elle (GatedButton).

	usePageHeader({ title: "Archived content" });

	const loadList = useCallback(
		async (pageNum: number) => {
			setLoading(true);
			setError(null);
			try {
				const res = await client.content.apiContentDeletedGet(
					pageNum,
					PAGE_SIZE,
					search.trim() || undefined,
					contentTypeFilter === "all" ? undefined : contentTypeFilter,
					languageFilter.trim() || undefined
				);
				const data = res.data;
				setItems(data?.items ?? []);
				setTotalCount(data?.totalCount ?? 0);
				setPage(data?.page ?? 1);
				setAvailableTypes(data?.contentTypes ?? []);
			} catch (err) {
				console.error(err);
				setError("Failed to load archived content.");
				setItems([]);
				setTotalCount(0);
				setAvailableTypes([]);
			} finally {
				setLoading(false);
			}
		},
		[search, contentTypeFilter, languageFilter]
	);

	useEffect(() => {
		loadList(page);
	}, [page, loadList]);

	const contentTypeLabelMap = useMemo(() => {
		const map = new Map<string, string>();
		(availableTypes ?? []).forEach((item) => {
			const key = (item?.key ?? "").trim();
			if (!key) return;
			map.set(key.toLowerCase(), item?.title ?? key);
		});
		return map;
	}, [availableTypes]);

	const resolveContentTypeLabel = useCallback(
		(key?: string | null) => {
			const normalized = (key ?? "").trim().toLowerCase();
			if (!normalized) return "";
			return contentTypeLabelMap.get(normalized) ?? key ?? "";
		},
		[contentTypeLabelMap]
	);

	const handleRestore = useCallback(
		async (item: BCCADMINBaseModelsContentListItem) => {
			const contentId = item.id ?? "";
			if (!contentId) return;
			setRestoring(contentId);
			setError(null);
			try {
				await client.content.apiContentRestorePost(contentId, { withCredentials: true });
				showSuccessModal({
					title: "Content restored",
					message: `"${item.title ?? item.slug ?? item.path ?? ""}" has been restored. You can publish it to preview again.`
				});
				await loadList(page);
			} catch (err: any) {
				console.error(err);
				const msg = err?.response?.data?.message ?? err?.response?.data ?? err?.message ?? "Restore failed.";
				setError(String(msg));
			} finally {
				setRestoring(null);
			}
		},
		[page, loadList]
	);

	const handlePurgeItem = useCallback(
		async (item: BCCADMINBaseModelsContentListItem) => {
			const contentId = item.id ?? "";
			if (!contentId) return;
			const label = item.title ?? item.slug ?? item.path ?? contentId;
			// eslint-disable-next-line no-restricted-globals, no-alert
			if (!window.confirm(`Supprimer définitivement « ${label} » ? Cette action est irréversible.`)) return;
			setPurging(contentId);
			setError(null);
			try {
				await purgeArchive({ ids: [contentId] });
				await loadList(page);
			} catch (err: any) {
				console.error(err);
				setError(err?.response?.data?.message ?? err?.message ?? "La purge a échoué.");
			} finally {
				setPurging(null);
			}
		},
		[page, loadList]
	);

	const handlePurgeAll = useCallback(async () => {
		setPurgingAll(true);
		setError(null);
		try {
			const result = await purgeArchive({ all: true });
			setShowPurgeAll(false);
			setPurgeAllConfirm("");
			showSuccessModal({
				title: "Archives purgées",
				message: `${result.purgedCount} élément(s) ont été définitivement supprimés.`
			});
			await loadList(1);
			setPage(1);
		} catch (err: any) {
			console.error(err);
			setError(err?.response?.data?.message ?? err?.message ?? "La purge a échoué.");
		} finally {
			setPurgingAll(false);
		}
	}, [loadList]);

	const goToItem = (item: BCCADMINBaseModelsContentListItem) => {
		const contentType = (item.contentType ?? "").toLowerCase();
		if (contentType === "page") {
			const langCode = item.language || DEFAULT_LANGUAGE;
			if (item.id) {
				history.push(`/page/${encodeURIComponent(item.id)}`);
			} else {
				history.push(`/page/${encodeURIComponent(langCode)}/${encodeURIComponent(item.slug || "")}`);
			}
			return;
		}

		if (contentType && item.id) {
			history.push(`/content/${encodeURIComponent(contentType)}/${encodeURIComponent(item.id)}`);
		}
	};

	const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
	const canPrev = page > 1;
	const canNext = page < totalPages;

	return (
		<div className="flex flex-col min-h-0 h-full">
			<div className="flex-1 min-h-0 overflow-y-auto p-6 space-y-4">
				<p className="text-sm text-slate-600">
					Archived content (pages and custom content types) appears here. Restore an item to make it active again.
				</p>

				{error && (
					<div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-700">
						{error}
					</div>
				)}

				<div className="flex flex-wrap items-center gap-2">
					<input
						className="w-full max-w-xl rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-slate-400 focus:outline-none"
						placeholder="Search by slug, path, title, or id"
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						onKeyDown={(e) => e.key === "Enter" && (setPage(1), loadList(1))}
					/>
					<select
						className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-slate-400 focus:outline-none"
						value={contentTypeFilter}
						onChange={(e) => {
							setContentTypeFilter(e.target.value);
							setPage(1);
						}}
					>
						<option value="all">All content types</option>
						{availableTypes.map((type) => (
							<option key={type.key ?? type.title} value={type.key ?? ""}>
								{type.title ?? type.key}
							</option>
						))}
					</select>
					<input
						className="w-40 rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-slate-400 focus:outline-none"
						placeholder="Language (optional)"
						value={languageFilter}
						onChange={(e) => setLanguageFilter(e.target.value)}
						onKeyDown={(e) => e.key === "Enter" && (setPage(1), loadList(1))}
					/>
					<button
						className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
						onClick={() => { setPage(1); loadList(1); }}
					>
						Search
					</button>
					<button
						className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
						onClick={() => loadList(page)}
						title="Refresh list"
					>
						<FiRefreshCw />
					</button>
					<GatedButton
						token="feature:deleted-pages:purge"
						className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-rose-300 bg-rose-50 px-3 py-2 text-sm font-semibold text-rose-700 hover:bg-rose-100 disabled:opacity-50"
						onClick={() => { setPurgeAllConfirm(""); setShowPurgeAll(true); }}
						disabled={totalCount === 0}
						title="Supprimer définitivement toutes les archives"
					>
						<FiTrash2 /> Purger toutes les archives
					</GatedButton>
				</div>

				{loading ? (
					<div className="flex items-center justify-center py-12">
						<LoadingSpinner size="lg" />
					</div>
				) : items.length === 0 ? (
					<div className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-slate-600">
						<FiTrash2 className="mx-auto h-10 w-10 text-slate-300 mb-3" />
						<p className="font-medium">No archived content</p>
						<p className="text-sm mt-1">When you delete content, it will appear here so you can restore it.</p>
					</div>
				) : (
					<div className="space-y-3">
						{items.map((item) => {
							const rowKey = item.id ?? `${item.contentType ?? ""}|${item.language ?? ""}|${item.slug ?? ""}`;
							const isThisRestoring = restoring === (item.id ?? null);
							const contentLabel = resolveContentTypeLabel(item.contentType);
							const displaySlug = item.slug || item.path || item.id || "";
							const displayTitle = item.title || displaySlug || "Untitled";
							const moveTargetId = item.move?.targetId;
							const moveTargetSlug = item.move?.targetSlug;
							return (
								<div
									key={rowKey}
									className="flex flex-wrap items-center justify-between gap-3 rounded-xl border-2 border-rose-200 bg-rose-50/80 p-4"
								>
									<div className="min-w-0">
										<p className="font-semibold text-rose-900">{displayTitle}</p>
										<p className="text-xs text-rose-700">
											{[displaySlug, item.language || DEFAULT_LANGUAGE, contentLabel].filter(Boolean).join(" · ")}
										</p>
										{(moveTargetId || moveTargetSlug) && (
											<p className="text-xs text-rose-700 mt-1">
												Moved to {moveTargetId || "unknown id"}{moveTargetSlug ? ` (${moveTargetSlug})` : ""}
											</p>
										)}
									</div>
									<div className="flex items-center gap-2 shrink-0">
										<button
											type="button"
											onClick={() => goToItem(item)}
											className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
											title="Open in editor"
										>
											<FiEdit2 className="h-3.5 w-3.5" /> Open
										</button>
										<button
											type="button"
											onClick={() => handleRestore(item)}
											disabled={restoring != null}
											className="inline-flex items-center gap-1 rounded-lg border border-emerald-200 bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
											title="Restore content"
										>
											{isThisRestoring ? (
												<LoadingSpinner size="sm" inline />
											) : (
												<FiRotateCcw className="h-3.5 w-3.5" />
											)}
											Restore
										</button>
										<GatedButton
											token="feature:deleted-pages:purge"
											onClick={() => handlePurgeItem(item)}
											disabled={purging != null}
											className="inline-flex items-center gap-1 rounded-lg border border-rose-300 bg-white px-3 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-100 disabled:opacity-60"
											title="Supprimer définitivement"
										>
											{purging === (item.id ?? null) ? (
												<LoadingSpinner size="sm" inline />
											) : (
												<FiTrash2 className="h-3.5 w-3.5" />
											)}
											Purger
										</GatedButton>
									</div>
								</div>
							);
						})}
					</div>
				)}

				{totalPages > 1 && (
					<div className="flex items-center justify-center gap-2 pt-4">
						<button
							type="button"
							onClick={() => setPage((p) => Math.max(1, p - 1))}
							disabled={!canPrev}
							className="rounded-lg border border-slate-200 px-3 py-2 text-sm disabled:opacity-50"
						>
							Previous
						</button>
						<span className="text-sm text-slate-600">
							Page {page} of {totalPages}
						</span>
						<button
							type="button"
							onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
							disabled={!canNext}
							className="rounded-lg border border-slate-200 px-3 py-2 text-sm disabled:opacity-50"
						>
							Next
						</button>
					</div>
				)}
			</div>

			{showPurgeAll && (
				<div className="fixed inset-0 z-[1300] flex items-center justify-center bg-slate-900/40 p-4">
					<div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
						<h2 className="flex items-center gap-2 text-lg font-bold text-rose-700">
							<FiTrash2 /> Purger toutes les archives
						</h2>
						<p className="mt-3 text-sm text-slate-700">
							Cette action supprime <span className="font-semibold">définitivement</span>{" "}
							<span className="font-semibold">{totalCount}</span> élément(s) archivé(s). Elle est
							irréversible et ne peut pas être annulée.
						</p>
						<p className="mt-3 text-sm text-slate-600">
							Pour confirmer, saisissez <span className="font-mono font-semibold">SUPPRIMER</span> :
						</p>
						<input
							autoFocus
							className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-rose-400 focus:outline-none"
							value={purgeAllConfirm}
							onChange={(e) => setPurgeAllConfirm(e.target.value)}
							placeholder="SUPPRIMER"
						/>
						<div className="mt-5 flex justify-end gap-2">
							<button
								type="button"
								onClick={() => { setShowPurgeAll(false); setPurgeAllConfirm(""); }}
								disabled={purgingAll}
								className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
							>
								Annuler
							</button>
							<button
								type="button"
								onClick={handlePurgeAll}
								disabled={purgeAllConfirm.trim() !== "SUPPRIMER" || purgingAll}
								className="inline-flex items-center gap-1.5 rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-50"
							>
								{purgingAll ? <LoadingSpinner size="sm" inline /> : <FiTrash2 className="h-4 w-4" />}
								Supprimer définitivement
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default DeletedPages;
